/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React from 'react';
import { useIntl } from 'react-intl';
import { connect } from "react-redux";
import { matchPath, useLocation } from 'react-router-dom';

import { List, ListItem, makeStyles } from "@material-ui/core";

import { ApplicationReducer, ApplicationState, UserReducer } from "../../../data/interfaces";
import CustomIcons from '../../../icons/CustomIcons'

import ListItemLink from './ListItemLink'
import { PATH_SEPARATOR } from "../../../data/constants";
import Settings from '../../../conf/settings';
import {
    isCredentialMenuAccessible,
    isProductionKeyGenerationAccessible,
    isSandboxKeyGenerationAccessible
} from '../../../data/utils';
import InfoTooltip from '../../../elements/CustomTooltip/InfoTooltip';
import { ApplicationPermission } from '../../../util/app-permissions';
import { AppPermission } from '../../../data/emus';

const useStyles = makeStyles((theme) => ({
    nested: {
        paddingLeft: theme.spacing(3.75),
    },
    nestedItem: {
        marginTop: -theme.spacing(3.5),
        marginBottom: -theme.spacing(3.5)
    }
}));

interface AppMenuItemsProps {
    applicationReducer: ApplicationReducer
    userReducer: UserReducer
}

interface AppCredentialUrlParams {
    env?: string;
    page: string;
}

interface AppUrlParams {
    page: string;
}

function AppMenuItems(props: AppMenuItemsProps) {
    const { applicationReducer, userReducer } = props;
    const classes = useStyles();
    const intl = useIntl();
    const appList = applicationReducer?.applications?.list;
    const application = appList?.length > 0 ? appList[0] : undefined;
    const applicationId = application ? application.applicationId : ':applicationId';
    const location = useLocation();
    const { selectedOrg, user, userInfo } = userReducer;
    const selectedOrgHandleName = selectedOrg.handle;
    const allowedScopes = user.allowedScopes;
        
    const isTabSelected = (page: string, credentialEnv?: string): boolean => {
        const currentPath = location.pathname;
        if (credentialEnv) {
            const { params } = matchPath<AppCredentialUrlParams>(currentPath, "/:orgName/applications/:applicationId/:page/:env") || {};
            return !!params && params.env === credentialEnv && params.page === page;
        }
        const { params } = matchPath<AppUrlParams>(currentPath, "/:orgName/applications/:applicationId/:page") || {};
        return !!params && params.page === page;
    }

    const menuItemsI18n = {
        overview: intl.formatMessage({id: 'application.menuItems.overview', defaultMessage: 'Overview'}),
        credentials: intl.formatMessage({
            id: 'application.menuItems.credentials',
            defaultMessage: 'Credentials'
        }),
        productionCredentials: intl.formatMessage({
            id: 'application.menuItems.credentials.productionKeys',
            defaultMessage: 'Production'
        }),
        sandboxCredentials: intl.formatMessage({
            id: 'application.menuItems.credentials.sandboxKeys',
            defaultMessage: 'Sandbox'
        }),
        apiKey: intl.formatMessage({
            id: 'application.menuItems.productionKeys.apiKey',
            defaultMessage: 'API Key'
        }),
        subscriptions: intl.formatMessage({id: 'application.menuItems.subscriptions', defaultMessage: 'Subscriptions'})
    };

    return (
        <div>
            <ListItemLink
                to={PATH_SEPARATOR + selectedOrgHandleName + "/applications/" + applicationId + "/overview"}
                primary={menuItemsI18n.overview}
                icon={<CustomIcons icon={isTabSelected('overview') ? 'overview-selected' : 'overview'} />}
                selected={isTabSelected('overview')}
                id='overview'
            />
            <>
                <InfoTooltip
                    content={intl.formatMessage({
                        id: 'application.menuItems.credentials.permission.denied.tooltip',
                        defaultMessage: "You don't have permission to view or generate credentials"
                    })}
                    darken={true}
                    isDisabled={isCredentialMenuAccessible(allowedScopes)}
                >
                    <span>
                        <ListItemLink
                            id="link-production-keys"
                            to={PATH_SEPARATOR + selectedOrgHandleName + "/applications/" + applicationId + "/credentials/" +
                                (Settings.sandboxKeyGenerationEnabled ? "sandbox" : "production") + "/oauth"}
                            primary={menuItemsI18n.credentials}
                            icon={
                                <CustomIcons
                                    icon={
                                        isTabSelected('credentials') ? 'credentials-selected' : 'credentials'
                                    }
                                />
                            }
                            selected={isTabSelected('credentials')}
                            disabled={!isCredentialMenuAccessible(allowedScopes)}
                        />
                    </span>
                </InfoTooltip>
                <List component="div" className={classes.nested}>
                    {Settings.sandboxKeyGenerationEnabled && (
                        <ListItem className={classes.nestedItem} data-testId="li-oauth-key-wrapper">
                            <InfoTooltip
                                content={intl.formatMessage({
                                    id: 'application.menuItems.credentials.sandboxKeys.permission.denied.tooltip',
                                    defaultMessage: "You don't have permission to view or generate sandbox credentials"
                                })}
                                darken={true}
                                isDisabled={isSandboxKeyGenerationAccessible(allowedScopes)}
                            >
                                <span>
                                    <ListItemLink
                                        to={PATH_SEPARATOR + selectedOrgHandleName + "/applications/" + applicationId + "/credentials/sandbox/oauth"}
                                        primary={menuItemsI18n.sandboxCredentials}
                                        selected={isTabSelected('credentials', 'sandbox')}
                                        id='sandbox-credentials-menu-item'
                                        disabled={!isSandboxKeyGenerationAccessible(allowedScopes)}
                                    />
                                </span>
                            </InfoTooltip>
                        </ListItem>
                    )}
                    <ListItem className={classes.nestedItem} data-testId="li-oauth-key-wrapper">
                        <InfoTooltip
                            content={intl.formatMessage({
                                id: 'application.menuItems.credentials.productionKeys.permission.denied.tooltip',
                                defaultMessage: "You don't have permission to view or generate production credentials"
                            })}
                            darken={true}
                            isDisabled={isProductionKeyGenerationAccessible(allowedScopes)}
                        >
                            <span>
                                <ListItemLink
                                    to={PATH_SEPARATOR + selectedOrgHandleName + "/applications/" + applicationId + "/credentials/production/oauth"}
                                    primary={menuItemsI18n.productionCredentials}
                                    selected={isTabSelected('credentials', 'production')}
                                    id='production-credentials-menu-item'
                                    disabled={!isProductionKeyGenerationAccessible(allowedScopes)}
                                />
                            </span>
                        </InfoTooltip>
                    </ListItem>
                </List>
            </>
            {ApplicationPermission.hasPermission(AppPermission.READ, application, userInfo, user.allowedScopes) && 
                <ListItemLink
                    to={PATH_SEPARATOR + selectedOrgHandleName + "/applications/" + applicationId + "/subscriptions"}
                    primary={menuItemsI18n.subscriptions}
                    icon={<CustomIcons icon={isTabSelected('subscriptions') ? 'subscriptions-selected' : 'subscriptions'} />}
                    selected={isTabSelected('subscriptions')}
                    id='subscriptions'
                />
            }
        </div>
    );
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        applicationReducer: state.applicationReducer,
        userReducer: state.userReducer
    }
};

export default connect(mapStateToProps)(AppMenuItems);
